/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Widget Order and Inquiries Successful Component
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.0.1
 */

import { FC, Fragment, memo } from 'react';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '../../../../../../components/common';
import { FlexStyled } from '../../../../../../components/common/Block/Flexbox';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetOrderInquiriesTab } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetOrderInquiriesOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import ICommonTranslation from '../../../../../../modules/internationalization/types/common';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { guardComponentType } from '../../../../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../../../../modules/role/selectors/guardSelector';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { useSetTableFilterAndSortMutation } from '../../../../../../modules/table/store';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { openRoute } from '../../../../../../routes/constants/externalRoutes';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import { WidgetListStyled, WidgetSuccessStyled } from '../../styled/WidgetStyled';
import { EWidgetSearchAccountsField, IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { IWidgetInquiries, IWidgetOrders } from '../widgetOrderInquiriesTypes';
import WidgetInquiriesList from './WidgetInquiriesList';
import { EWidgetOrderInquiries } from './WidgetOrderInquiries';
import WidgetOrdersList from './WidgetOrderList';

export interface IWidgetOrderInquiriesSuccessful {
    className?: string;
    isAllowToCreateInquiry?: boolean;
    items: IWidgetInquiries[] | IWidgetOrders[];
    openCreateInquiry?: () => void;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    translation: IPageDashboardTranslation & ICommonTranslation;
    type: EWidgetOrderInquiries;
}

export const WidgetOrderInquiriesSuccessful: FC<IWidgetOrderInquiriesSuccessful & IConnectedProps> = (props) => {
    const { items, translation, type, className } = props;

    const [filter] = useSetTableFilterAndSortMutation();

    const onOrderLinkClick = (): void => {
        const item = translation.widgets.orderInquiries.order.successful.btnText;
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries.clickLinkOrderOrInquiryWidgetOrderInquiries(
                item,
                EGTMWidgetOrderInquiriesTab.orders,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        filter({ url: 'orders/filter-and-sort', filter: {} }).then(() => {
            openRoute(RoutePath.orders);
        });
    };

    const getUrlToInquiries = () => {
        const filters: any = {};
        if (props.selectedAccount[EWidgetSearchAccountsField.number]) {
            filters.account = [{
                value: props.selectedAccount[EWidgetSearchAccountsField.number],
                label: props.selectedAccount[EWidgetSearchAccountsField.number],
            }];
        }
        const filter: string = encodeURIComponent(JSON.stringify(filters));

        return `${RoutePath.inquiries}?f=${filter}`;
    };

    const onInquiryLinkClick = (): void => {
        const item = translation.widgets.orderInquiries.inquiries.successful.btnText;
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetOrderInquiries.clickLinkOrderOrInquiryWidgetOrderInquiries(
                item,
                EGTMWidgetOrderInquiriesTab.inquiries,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    return (
        <Fragment>
            {
                {
                    order: (
                        <WidgetSuccessStyled className={`widget-order-inquiries__success-orders ${className || ''}`}>
                            <WidgetListStyled>
                                <WidgetOrdersList
                                    orders={items as IWidgetOrders[]}
                                    pushGtmAnalytic={props.pushGtmAnalytic}
                                />
                            </WidgetListStyled>
                        </WidgetSuccessStyled>
                    ),
                    inquiries: (
                        <WidgetSuccessStyled className={`widget-order-inquiries__success-inquiries ${className || ''}`}>
                            <WidgetListStyled>
                                <WidgetInquiriesList
                                    componentsFeature={props.componentsFeature}
                                    inquiries={items as IWidgetInquiries[]}
                                    pushGtmAnalytic={props.pushGtmAnalytic}
                                />
                            </WidgetListStyled>
                        </WidgetSuccessStyled>
                    ),
                }[type]
            }
            {
                {
                    order: (
                        <div>
                            <LinkBlock dataName="redirectToOrders" onClick={onOrderLinkClick}>
                                {translation.widgets.orderInquiries.order.successful.btnText}
                            </LinkBlock>
                        </div>
                    ),
                    inquiries: (
                        <FlexStyled justifyContent="space-between">
                            <div>
                                <LinkBlock
                                    dataName="redirectToInquiries"
                                    url={getUrlToInquiries()}
                                    onClick={onInquiryLinkClick}
                                >
                                    {translation.widgets.orderInquiries.inquiries.successful.btnText}
                                </LinkBlock>
                            </div>
                            {props.isAllowToCreateInquiry && (
                                <div>
                                    <LinkBlock data-name="openCreateInquiryModal" onClick={props.openCreateInquiry}>
                                        {translation.widgets.orderInquiries.inquiries.empty.btnText}
                                    </LinkBlock>
                                </div>
                            )}
                        </FlexStyled>
                    ),
                }[type]
            }
        </Fragment>
    );
};

export interface IConnectedProps {
    componentsFeature: guardComponentType;
    selectedAccount: IWidgetSearchAccountsItem;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    componentsFeature: makeSelectGuardComponents(),
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
});

export default connect(mapStateToProps)(memo(WidgetOrderInquiriesSuccessful));
